import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Spinner } from '@vwfs-bronson/bronson-react';
import './services/i18n/i18n';

import { store } from './services/redux/root.reducer';
import { getStyles } from './services/brand';
import { getBrandName } from './config';
import { analytics } from './services/analytics/analytics';

const ConnectedApp = React.lazy(() => import('./App'));

const PositionRestore = (): null => {
  const location = useLocation();

  React.useEffect(() => {
    let retryTimeout: number | undefined;

    if (!location.hash) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });

      return;
    }

    const id = location.hash.replace('#', '');

    const initialTimeout = window.setTimeout(() => {
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        return;
      }

      retryTimeout = window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({
          behavior: 'smooth',
        });
      }, 1000);
    }, 50);

    return () => {
      window.clearTimeout(initialTimeout);

      if (retryTimeout !== undefined) {
        window.clearTimeout(retryTimeout);
      }
    };
  }, [location.hash, location.pathname]);

  return null;
};

export const Init = () => {
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    getStyles(getBrandName()).then(() => {
      if (active) {
        setLoaded(true);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    analytics.initTracking();
  }, []);

  if (!loaded) {
    return null;
  }

  return (
    <React.Suspense fallback={<Spinner fullPage />}>
      <Provider store={store}>
        <BrowserRouter basename={process.env.REACT_APP_BASE_PATH}>
          <PositionRestore />
          <ConnectedApp />
        </BrowserRouter>
      </Provider>
    </React.Suspense>
  );
};

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element was not found');
}

const root = createRoot(rootElement);

root.render(<Init />);

declare const module: {
  hot?: {
    accept(): void;
  };
};

module.hot?.accept();
